import { useEffect, useState, memo, useMemo } from 'react';
import {
  filter,
  isEmpty,
  isNumber,
  isString,
  last,
  nth,
  toInteger,
} from 'lodash';
import { SubMenu, CustomRangeItem } from './slicer-option-sub-menu.styles';
import { TimestampSlicerTypes } from '../../../interfaces';
import { TimeFrameDropdown } from '../time-frame-dropdown';
import { SlicerTimeRangeInfo } from '../slicer-timerange-info';
import { messages } from '../../../../../i18n';
import { useHasValueChanged } from '../../../../../common/utilities/state-helpers.hook';
import { TimePeriods } from '../../../../filter/relative-date-condition';
import { toStrings } from '../slicer-timerange-info/slicer-timerange-info.hook';

export interface ISlicerOptionSubMenuProps {
  type: TimestampSlicerTypes;
  setOperands: (options: string[]) => void;
  fieldName: string;
  defaultValue?: string[];
}

const matchesIntegerType = strNumber => /^\d+$/.test(strNumber);
const pullDigits = (str: string) => {
  return filter(str.split(''), char => matchesIntegerType(char)).join('');
};

export const TimeSlicerOptionSubMenu = memo<ISlicerOptionSubMenuProps>(
  ({ type, setOperands, fieldName, defaultValue = [] }) => {
    const defaultValues = useMemo(() => {
      const firstOperand = nth(defaultValue, 0);
      const timePeriod = (nth(defaultValue, 1) ??
        '') as keyof typeof TimePeriods;
      const isPartial =
        type !== TimestampSlicerTypes.CURRENT_AND_PREV &&
        last(defaultValue) === 'true';
      return {
        scalar:
          isString(firstOperand) &&
          !isEmpty(firstOperand) &&
          matchesIntegerType(firstOperand) &&
          !isEmpty(timePeriod)
            ? toInteger(firstOperand)
            : undefined,
        timePeriod,
        isPartial,
      };
    }, [defaultValue, type]);

    const [scalarValue, setScalarValue] = useState<number>(
      defaultValues.scalar,
    );
    const [timePeriod, setTimePeriod] = useState<keyof typeof TimePeriods>(
      defaultValues.timePeriod,
    );
    const [isPartialTimePeriod, setIsPartialTimePeriod] = useState<boolean>(
      defaultValues.isPartial,
    );

    const handleChangeCustomRange = event => {
      const { value } = event.target;

      const digits = pullDigits(value);
      setScalarValue(isEmpty(digits) ? undefined : toInteger(digits));
    };

    const hasScalarValueChanged = useHasValueChanged({
      value: scalarValue,
      defaultValue: scalarValue,
    });
    const hasTimePeriodChanged = useHasValueChanged({
      value: timePeriod,
      defaultValue: timePeriod,
    });
    const hasPartialChanged = useHasValueChanged({
      value: isPartialTimePeriod,
      defaultValue: isPartialTimePeriod,
    });

    const hasOperands = useMemo(
      () => isNumber(scalarValue) && !isEmpty(timePeriod),
      [scalarValue, timePeriod],
    );

    useEffect(() => {
      if (
        !(hasScalarValueChanged || hasTimePeriodChanged || hasPartialChanged) ||
        !hasOperands
      ) {
        return;
      }

      setOperands(
        toStrings([
          scalarValue,
          timePeriod,
          type !== TimestampSlicerTypes.CURRENT_AND_PREV && isPartialTimePeriod,
        ]),
      );
    }, [
      hasOperands,
      hasPartialChanged,
      hasScalarValueChanged,
      hasTimePeriodChanged,
      isPartialTimePeriod,
      scalarValue,
      setOperands,
      timePeriod,
      type,
    ]);

    return (
      <SubMenu>
        <CustomRangeItem>
          <input
            className='form-control'
            type='text'
            pattern='\d+'
            placeholder={messages.dataFormats.number}
            value={scalarValue}
            onChange={handleChangeCustomRange}
          />
        </CustomRangeItem>
        <TimeFrameDropdown
          onMenuItemClick={setTimePeriod}
          selectedValue={timePeriod}
        />
        {hasOperands && (
          <SlicerTimeRangeInfo
            scalarValue={scalarValue}
            timePeriod={timePeriod}
            includeCurrentPartial={isPartialTimePeriod}
            type={type}
            fieldName={fieldName}
            includeCurrentPartialClickHandler={setIsPartialTimePeriod}
          />
        )}
      </SubMenu>
    );
  },
);
